import { env } from '../env';
import { buildStorefrontCustomProductCollectionPath } from './storefront-engine/custom-product-collections';
import type { StorefrontCustomProductCollectionRef } from '../types/storefront-engine';
import {
  buildLegacyStorefrontPath,
  buildRootStorefrontUrl,
  buildTenantStorefrontUrl,
  getStorefrontBaseDomainFromOrigin,
  hasControlOrBidi,
  normalizeStoreSlug,
} from './storefront-location';

export interface BuildProductUrlInput {
  readonly storeSlug: string;
  readonly productId: string;
}

export interface BuildCustomProductCollectionUrlInput {
  readonly storeSlug: string;
  readonly collectionRef: StorefrontCustomProductCollectionRef;
}

function normalizePublicStoreSlugOrThrow(slug: string): string {
  const normalized = normalizeStoreSlug(slug);
  if (!normalized) {
    throw new Error('Invalid storefront slug');
  }
  return normalized;
}

/**
 * Build the public-facing URL for a published storefront.
 *
 * Reads `VITE_PUBLIC_STOREFRONT_URL` as the platform/root storefront origin
 * and returns the canonical tenant-host URL for a published store.
 */
export function buildStoreUrl(slug: string): string {
  return buildTenantStorefrontUrl(env.VITE_PUBLIC_STOREFRONT_URL, {
    storeSlug: normalizePublicStoreSlugOrThrow(slug),
  });
}

export function buildRootLegacyStoreUrl(slug: string): string {
  return buildRootStorefrontUrl(env.VITE_PUBLIC_STOREFRONT_URL, {
    path: buildLegacyStorefrontPath({
      storeSlug: normalizePublicStoreSlugOrThrow(slug),
    }),
  });
}

export function buildRootLegacyProductUrl(input: BuildProductUrlInput): string {
  return buildRootStorefrontUrl(env.VITE_PUBLIC_STOREFRONT_URL, {
    path: buildLegacyStorefrontPath({
      storeSlug: normalizePublicStoreSlugOrThrow(input.storeSlug),
      path: `/p/${encodeURIComponent(input.productId)}`,
    }),
  });
}

export function buildRootLegacyCartUrl(slug: string): string {
  return buildRootStorefrontUrl(env.VITE_PUBLIC_STOREFRONT_URL, {
    path: buildLegacyStorefrontPath({
      storeSlug: normalizePublicStoreSlugOrThrow(slug),
      path: '/cart',
    }),
  });
}

export function buildRootLegacyCheckoutUrl(slug: string): string {
  return buildRootStorefrontUrl(env.VITE_PUBLIC_STOREFRONT_URL, {
    path: buildLegacyStorefrontPath({
      storeSlug: normalizePublicStoreSlugOrThrow(slug),
      path: '/checkout',
    }),
  });
}

export function buildStoreBuilderCanvasUrl(slug: string): string {
  return buildRootStorefrontUrl(env.VITE_PUBLIC_STOREFRONT_URL, {
    path: buildLegacyStorefrontPath({
      storeSlug: normalizePublicStoreSlugOrThrow(slug),
      path: '/builder-canvas',
    }),
  });
}

export function buildStorePreviewUrl(
  slug: string,
  tokenFragment: `preview_token=${string}`,
): string {
  return buildRootStorefrontUrl(env.VITE_PUBLIC_STOREFRONT_URL, {
    path: buildLegacyStorefrontPath({
      storeSlug: normalizePublicStoreSlugOrThrow(slug),
      path: '/preview',
    }),
    hash: tokenFragment,
  });
}

/**
 * Build the public-facing URL for a published storefront product.
 *
 * Shared by product contact, share actions, and SEO metadata so
 * WhatsApp messages, copied links, canonical URLs, OG URLs, and JSON-LD
 * offers cannot drift.
 */
export function buildProductUrl(input: BuildProductUrlInput): string {
  return buildTenantStorefrontUrl(env.VITE_PUBLIC_STOREFRONT_URL, {
    storeSlug: normalizePublicStoreSlugOrThrow(input.storeSlug),
    path: `/p/${encodeURIComponent(input.productId)}`,
  });
}

export function buildCustomProductCollectionUrl(
  input: BuildCustomProductCollectionUrlInput,
): string {
  const legacyPath = buildStorefrontCustomProductCollectionPath(
    normalizePublicStoreSlugOrThrow(input.storeSlug),
    input.collectionRef,
  );
  return buildTenantStorefrontUrl(env.VITE_PUBLIC_STOREFRONT_URL, {
    storeSlug: normalizePublicStoreSlugOrThrow(input.storeSlug),
    path: legacyPath.replace(/^\/s\/[^/]+/, ''),
  });
}

export function normalizePublicStorefrontOrigin(publicStorefrontOrigin: string): string {
  const trimmed = publicStorefrontOrigin.trim();
  if (!trimmed || hasControlOrBidi(trimmed)) {
    throw new Error('Invalid public storefront origin');
  }

  const parsed = new URL(trimmed);
  if (
    parsed.username ||
    parsed.password ||
    parsed.pathname !== '/' ||
    parsed.search ||
    parsed.hash
  ) {
    throw new Error('Invalid public storefront origin');
  }
  return parsed.origin;
}

export function buildPromotionUrl(input: {
  readonly publicStorefrontOrigin: string;
  readonly publicPath: string;
  readonly token: string;
}): string {
  const origin = normalizePublicStorefrontOrigin(input.publicStorefrontOrigin);
  if (!input.publicPath.startsWith('/s/') || hasControlOrBidi(input.publicPath)) {
    throw new Error('Invalid promotion public path');
  }
  const legacyPath = new URL(input.publicPath, origin);
  const legacyMatch = /^\/s\/([^/]+)(\/.*)?$/.exec(legacyPath.pathname);
  if (!legacyMatch?.[1]) {
    throw new Error('Invalid promotion public path');
  }
  const decodedSlug = decodeURIComponent(legacyMatch[1]);
  const storeSlug = normalizePublicStoreSlugOrThrow(decodedSlug);
  const tenantPath = legacyMatch[2] && legacyMatch[2] !== '' ? legacyMatch[2] : '/';
  const url = new URL(
    buildTenantStorefrontUrl(origin, {
      storeSlug,
      path: tenantPath,
      search: legacyPath.search,
      hash: legacyPath.hash,
    }),
  );
  url.searchParams.set('pk', input.token);
  url.searchParams.set('utm_source', 'dukanti_promotion');
  assertPromotionUrlOnStorefrontOrigin({
    publicUrl: url.toString(),
    publicStorefrontOrigin: origin,
  });
  return url.toString();
}

export function assertPromotionUrlOnStorefrontOrigin(input: {
  readonly publicUrl: string;
  readonly publicStorefrontOrigin: string;
}): void {
  const expectedBaseDomain = getStorefrontBaseDomainFromOrigin(
    normalizePublicStorefrontOrigin(input.publicStorefrontOrigin),
  );
  const actual = new URL(input.publicUrl);
  const isTenantHost =
    actual.hostname.endsWith(`.${expectedBaseDomain}`) &&
    normalizeStoreSlug(actual.hostname.slice(0, -(expectedBaseDomain.length + 1))) !== null;
  if (!isTenantHost || actual.pathname.startsWith('/s/')) {
    throw new Error('Promotion URL is not on the configured storefront origin');
  }
}
